import { computed } from 'vue'
import { useRoute } from 'vue-router'

/** A page widget's map of route name → the modals that route opens. Declare it `as const`. */
export type ModalRegistry = Readonly<Record<string, readonly string[]>>

/**
 * Which modals the current route opens, for a page widget that renders its modals by route
 * rather than by a local `ref`: `v-if="hasModal('edit')"`.
 *
 * Keep the registry `as const` — that is what narrows the argument to the keys the widget
 * actually declares, so `hasModal('typo')` stops compiling instead of quietly answering
 * false for ever.
 */
export function useRouteModal<TRegistry extends ModalRegistry>(
  registry: TRegistry
) {
  type ModalKey = TRegistry[keyof TRegistry][number]

  const route = useRoute()

  const openedModals = computed<readonly ModalKey[]>(() => {
    const name = route.name
    if (typeof name !== 'string') return []

    return (registry[name] as readonly ModalKey[] | undefined) ?? []
  })

  function hasModal(name: ModalKey) {
    return openedModals.value.includes(name)
  }

  return { hasModal }
}
